import React from "react";
import { connect } from "react-redux";
import CoreEngine from "../../core/CoreEngine";
import RequestEngine from "../../core/RequestEngine";
import SocketEngine from "../../core/SocketEngine";
import loadingAction from "../../core/redux/actions/loadingAction";
import strings from "../../core/translate";
import SelectRow from "../../components/Select/SelectRow";
import PageHeader from "../../components/Page/PageHeader";
import EmptyState from "../../components/Page/EmptyState";
import { getGarageSections } from "../../shared/garage-sections";
import { FormModal } from "../../components/Form/FormKit";
import AppointmentDetail from "../merchants-branches-appointments/AppointmentDetail";
import "../../assets/css/dashboard.css";

/**
 * The queues a garage day is made of, in the order the work moves through
 * them: an appointment is asked for, confirmed, the car arrives, it is
 * inspected and quoted, the work runs, the invoice is settled, the car is
 * parked up, and finally it goes home.
 *
 * `tone` names a reserved colour (see dashboard.css) that follows the queue
 * everywhere: its tile, the left edge of each of its records, its badges, and
 * its column on the admin overview. `group` is only used there, to gather the
 * eight columns into the four questions an admin actually asks.
 */
const QUEUES = [
  {
    key: "pendingAppointments",
    tone: "pending",
    group: "appointments",
    icon: "nc-icon nc-watch-time",
    label: () => strings.q_pending_appointments,
    hint: () => strings.q_pending_appointments_hint,
  },
  {
    key: "acceptedAppointments",
    tone: "accepted",
    group: "appointments",
    icon: "nc-icon nc-calendar-60",
    label: () => strings.q_accepted_appointments,
    hint: () => strings.q_accepted_appointments_hint,
  },
  {
    key: "openJobCards",
    tone: "open",
    group: "bay",
    icon: "nc-icon nc-paper",
    label: () => strings.q_open_job_cards,
    hint: () => strings.q_open_job_cards_hint,
  },
  {
    key: "awaitingApproval",
    tone: "awaiting",
    group: "bay",
    icon: "nc-icon nc-chat-33",
    label: () => strings.q_awaiting_approval,
    hint: () => strings.q_awaiting_approval_hint,
  },
  {
    key: "inProgressJobCards",
    tone: "progress",
    group: "bay",
    icon: "nc-icon nc-user-run",
    label: () => strings.q_in_progress_job_cards,
    hint: () => strings.q_in_progress_job_cards_hint,
  },
  {
    key: "unpaid",
    tone: "unpaid",
    group: "money",
    icon: "nc-icon nc-money-coins",
    label: () => strings.q_unpaid,
    hint: () => strings.q_unpaid_hint,
  },
  {
    key: "readyToDeliver",
    tone: "ready",
    group: "handover",
    icon: "nc-icon nc-check-2",
    label: () => strings.q_ready_to_deliver,
    hint: () => strings.q_ready_to_deliver_hint,
  },
  {
    key: "delivered",
    tone: "delivered",
    group: "handover",
    icon: "nc-icon nc-delivery-fast",
    label: () => strings.q_delivered,
    hint: () => strings.q_delivered_hint,
  },
];

const COLUMN_GROUPS = [
  { key: "appointments", label: () => strings.col_appointments },
  { key: "bay", label: () => strings.col_in_bay },
  { key: "money", label: () => strings.q_unpaid },
  { key: "handover", label: () => strings.col_handover },
];

const toneVars = (tone) => ({
  "--q": `var(--q-${tone})`,
  "--q-soft": `var(--q-${tone}-soft)`,
});

// Whether the reader last left the queues minimized. Remembered because a
// dashboard is reloaded all day and re-minimizing it every time is the kind
// of small tax that ends with the page left alone.
const OVERVIEW_COLLAPSED_KEY = "dashboardOverviewCollapsed";

const readOverviewCollapsed = () => {
  try {
    return localStorage.getItem(OVERVIEW_COLLAPSED_KEY) === "1";
  } catch (err) {
    return false;
  }
};

// The garage's own calendar day, not UTC: appointment dates are stored the way
// the garage writes them, so toISOString() would shift the day either side of
// midnight.
const localDay = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

/**
 * Two dashboards behind one screen, because two people ask different
 * questions of it.
 *
 * A garage — a garage login, or an admin who picked one — asks "what do I do
 * next", and gets its day broken into queues with the records inside each.
 *
 * An admin looking at every garage asks "which of my garages needs me", and
 * gets those same queues rolled up per garage, so the columns can be compared
 * down the page. The only records named there are the ones that cannot wait:
 * cars past the date they were promised back.
 *
 * Both report from today, and the three kinds of queue answer to time
 * differently. Appointments look forward: every unanswered request whatever
 * day it is for, and confirmed work from today onwards. Cars in the bay carry
 * over, because one dropped off on Monday and still open on Wednesday is
 * Wednesday's work too. A handover is an event rather than a state, so it
 * counts only on the day it happened.
 *
 * The queues are stages, not a partition. A car stays under its job card for
 * the whole of its stay and shows again under the stage it has reached, so
 * quoting a car moves nothing off the floor.
 */
class GarageDashboard extends CoreEngine {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      merchants: [],
      branches: [],
      selectedMerchant: "",
      selectedBranch: "", // empty means every garage of the merchant
      garageScoped: false,
      // Always today. There is no date picker for now, but the day is still
      // re-read on every load, so a tab left open overnight rolls over.
      day: localDay(new Date()),
      garageData: null, // one garage's day
      merchantData: null, // every garage of the merchant, rolled up
      focus: QUEUES[0].key,
      // the queues minimized to a chip row, as the app's overview does it
      overviewCollapsed: readOverviewCollapsed(),
      // set only when the reader picks a queue by hand, so the next socket
      // refresh does not move them off it
      focusPinned: false,
      // the api answered with nothing: say so rather than drawing a day of
      // zeros that reads as a quiet garage
      failed: false,
      detailOf: null,
    };
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    const merchantIdFromStorage = sessionStorage.getItem("merchantId");
    const garageBranchId = sessionStorage.getItem("garageBranchId");

    SocketEngine.on("job_card:updated", this.handleGarageActivity);
    SocketEngine.on("booking:updated", this.handleGarageActivity);
    SocketEngine.on("work_order:updated", this.handleGarageActivity);

    if (garageBranchId) {
      // a garage login only ever reports on its own garage
      this.setState(
        {
          garageScoped: true,
          selectedBranch: garageBranchId,
          selectedMerchant: merchantIdFromStorage || "",
        },
        this.load
      );
      return;
    }

    if (merchantIdFromStorage) {
      this.setState({ selectedMerchant: merchantIdFromStorage }, () => {
        this.fetchBranches();
        this.load();
      });
    } else {
      this.fetchMerchants();
    }
  }

  componentWillUnmount() {
    SocketEngine.off("job_card:updated", this.handleGarageActivity);
    SocketEngine.off("booking:updated", this.handleGarageActivity);
    SocketEngine.off("work_order:updated", this.handleGarageActivity);
  }

  /**
   * Anything that moves at a garage changes these figures. The overview spans
   * every garage, so it reloads on any of them; a single garage reloads only
   * on its own.
   */
  handleGarageActivity = (payload) => {
    const { selectedBranch } = this.state;
    if (
      selectedBranch &&
      payload &&
      payload.branchId &&
      payload.branchId !== selectedBranch
    ) {
      return;
    }
    this.load();
  };

  fetchMerchants = async () => {
    const response = await this.engine.getItem("merchants");
    if (response && response.status === 200) {
      const merchants = response.data.items.map((merchant) => ({
        value: merchant.id,
        label: merchant.name,
      }));
      this.setState({ merchants, isLoading: false }, () => {
        if (merchants.length === 1) {
          this.selectMerchant(merchants[0].value);
        }
      });
    }
  };

  fetchBranches = async () => {
    const { selectedMerchant } = this.state;
    if (!selectedMerchant) return;
    try {
      const response = await this.engine.getItem(
        `merchants/${selectedMerchant}/branches`
      );
      if (response && response.status === 200) {
        const items = response.data.items || [];
        this.setState({
          branches: items.map((branch) => ({
            value: branch.id,
            label: branch.name || branch.address,
          })),
        });
      }
    } catch (err) {
      // a garage login may not read the merchant's garages, and does not need to
      this.setState({ branches: [] });
    }
  };

  selectMerchant = (merchantId) => {
    this.setState(
      {
        selectedMerchant: merchantId,
        selectedBranch: "",
        branches: [],
        garageData: null,
        merchantData: null,
        focusPinned: false,
      },
      () => {
        this.fetchBranches();
        this.load();
      }
    );
  };

  load = async () => {
    const { selectedMerchant, selectedBranch, garageScoped } = this.state;
    if (!selectedMerchant && !selectedBranch) return;

    const day = localDay(new Date());
    this.setState({ isLoading: true, day });

    // One garage — its own, or the one an admin picked.
    if (selectedBranch || garageScoped) {
      const path = `merchants-branches/${selectedBranch}/dashboard?date=${day}`;
      const garageData = await this.fetchOne(path);
      this.setState((state) => ({
        isLoading: false,
        garageData,
        failed: !garageData,
        focus:
          state.focusPinned || !garageData
            ? state.focus
            : this.firstBusyQueue(garageData.summary),
      }));
      this.props.loadingAction(false);
      return;
    }

    // Every garage of the merchant, rolled up — one request, not one per garage.
    const merchantData = await this.fetchOne(
      `merchants/${selectedMerchant}/dashboard?date=${day}`
    );
    this.setState({ isLoading: false, merchantData, failed: !merchantData });
    this.props.loadingAction(false);
  };

  fetchOne = async (path) => {
    try {
      const response = await this.engine.getItem(path);
      if (!response || response.status !== 200) return null;
      return response.data;
    } catch (err) {
      return null;
    }
  };

  firstBusyQueue(summary) {
    const busy = QUEUES.find((queue) => (summary[queue.key] || 0) > 0);
    return (busy || QUEUES[0]).key;
  }

  selectBranch = (branchId) => {
    this.setState(
      {
        selectedBranch: branchId || "",
        garageData: null,
        focusPinned: false,
      },
      this.load
    );
  };

  /**
   * Starting service on a confirmed appointment means receiving the car: the
   * garage opens its job card, which is also what moves the booking to "in
   * progress". There is no separate button that only flips a status — a car
   * that has started is a car that arrived, and the card is the record of it.
   */
  startService = (row) => {
    const branchId = row.branchId || this.state.selectedBranch;
    if (!branchId || !row.bookingId) return;
    this.props.history.push(
      `/admin/merchants-branches/${branchId}/job-cards/new/${row.bookingId}`
    );
  };

  /**
   * A record opens where the work on it happens: its work order if one exists,
   * the car-and-customer dialog for an appointment that has none yet, and the
   * garage's job cards for a walk-in that has neither.
   */
  openRecord = (row) => {
    if (row.workOrderId) {
      this.props.history.push({
        pathname: `/admin/work-orders/${row.workOrderId}/manage`,
        state: {
          workOrder: { id: row.workOrderId },
          booking: row.bookingId ? { id: row.bookingId } : null,
          meta: {
            customer: row.customerName,
            vehicle: [row.vehicleMake, row.vehicleModel]
              .filter(Boolean)
              .join(" "),
          },
        },
      });
      return;
    }

    if (row.appointmentId) {
      this.setState({ detailOf: row });
      return;
    }

    if (row.jobCardId && row.branchId) {
      this.props.history.push(
        `/admin/merchants-branches/${row.branchId}/job-cards`
      );
    }
  };

  toggleOverview = () => {
    this.setState(
      (state) => ({ overviewCollapsed: !state.overviewCollapsed }),
      () => {
        try {
          localStorage.setItem(
            OVERVIEW_COLLAPSED_KEY,
            this.state.overviewCollapsed ? "1" : "0"
          );
        } catch (err) {
          // a browser that refuses storage still gets the toggle for this visit
        }
      }
    );
  };

  /**
   * The eight queue tiles. On one garage they are the filter for the records
   * below; on the overview they are the merchant's totals and nothing more,
   * because the records live in the table under them.
   *
   * Minimized — the same toggle the app's overview carries — the tiles become
   * a row of chips: the same eight queues, the same counts, the same filter,
   * in a fifth of the height, so the records get the screen instead.
   */
  renderTiles(summary, interactive) {
    const { focus, overviewCollapsed } = this.state;

    return (
      <section className="queue-section">
        <div className="section-head">
          <span className="section-head__label">{strings.overview}</span>
          <button
            type="button"
            className="section-head__toggle"
            onClick={this.toggleOverview}
            aria-expanded={!overviewCollapsed}
            aria-controls="dashboard-queues"
          >
            {overviewCollapsed ? strings.expand : strings.minimize}
            <i
              className={`nc-icon ${
                overviewCollapsed ? "nc-minimal-down" : "nc-minimal-up"
              }`}
            />
          </button>
        </div>

        <div
          id="dashboard-queues"
          className={overviewCollapsed ? "queue-chips" : "queue-grid"}
        >
          {QUEUES.map((queue) => {
            const count = summary[queue.key] || 0;
            const active = interactive && focus === queue.key;
            const pick = interactive
              ? () => this.setState({ focus: queue.key, focusPinned: true })
              : undefined;

            if (overviewCollapsed) {
              return (
                <button
                  type="button"
                  key={queue.key}
                  className={`queue-chip${active ? " is-active" : ""}${
                    interactive ? "" : " is-static"
                  }`}
                  style={toneVars(queue.tone)}
                  title={`${queue.label()} — ${queue.hint()}`}
                  aria-pressed={interactive ? active : undefined}
                  onClick={pick}
                >
                  <i className={queue.icon} />
                  <span className="queue-chip__value">{count}</span>
                  <span className="queue-chip__label">
                    {strings[`q_short_${queue.key}`] || queue.label()}
                  </span>
                </button>
              );
            }

            return (
              <button
                type="button"
                key={queue.key}
                className={`queue-tile${active ? " is-active" : ""}${
                  interactive ? "" : " is-static"
                }`}
                style={toneVars(queue.tone)}
                aria-pressed={interactive ? active : undefined}
                onClick={pick}
              >
                <span className="queue-tile__icon">
                  <i className={queue.icon} />
                </span>
                <span className="queue-tile__value">{count}</span>
                <span className="queue-tile__label">{queue.label()}</span>
                <span className="queue-tile__hint">{queue.hint()}</span>
              </button>
            );
          })}
        </div>
      </section>
    );
  }

  // ─── one garage ────────────────────────────────────────────────────────────

  renderTags(row, queue) {
    const tags = [];

    // What the record is doing right now, said once: an appointment is named
    // by its booking, everything that reached the floor by its work order.
    if (row.workOrderStatus) {
      tags.push(
        <span key="wo" className={`chip is-${queue.tone}`}>
          {strings[`wo_status_${row.workOrderStatus}`] || row.workOrderStatus}
        </span>
      );
    } else if (row.jobCardId) {
      tags.push(
        <span key="none" className="chip is-open">
          {strings.no_work_order}
        </span>
      );
    } else if (row.bookingStatus) {
      tags.push(
        <span key="booking" className={`chip is-${queue.tone}`}>
          {strings[`booking_${row.bookingStatus}`] || row.bookingStatus}
        </span>
      );
    }

    // Money rides along on every row rather than being a stage of its own, so
    // the same car is never listed twice under two different headings.
    if (row.unpaid && queue.key !== "unpaid") {
      tags.push(
        <span key="unpaid" className="chip is-unpaid">
          {strings[`payment_${row.paymentStatus}`] || strings.payment_unpaid}
        </span>
      );
    } else if (queue.key === "unpaid" && row.paymentStatus) {
      tags.push(
        <span key="payment" className="chip is-unpaid">
          {strings[`payment_${row.paymentStatus}`] || row.paymentStatus}
        </span>
      );
    }

    if (row.overdue) {
      tags.push(
        <span key="late" className="chip is-unpaid">
          {strings.overdue}
        </span>
      );
    }

    // A record for another day — a pending request for next week, or a car
    // dropped off before today. Name the day, or it reads as today's.
    if (row.date && row.date !== this.state.day) {
      tags.push(
        <span key="day" className="chip">
          {row.date}
        </span>
      );
    }

    return tags;
  }

  renderRecord(row, queue) {
    return (
      <li
        key={row.key}
        className="record"
        role="button"
        tabIndex={0}
        onClick={() => this.openRecord(row)}
        onKeyDown={(event) => event.key === "Enter" && this.openRecord(row)}
      >
        <span className="record__time">
          {row.time || "—"}
          {!row.bookingId && <small>{strings.walk_in}</small>}
        </span>

        <span className="record__main">
          <span className="record__plate">{row.plateNumber || "—"}</span>
          <span className="record__vehicle">
            {[row.vehicleMake, row.vehicleModel, row.vehicleYear]
              .filter(Boolean)
              .join(" ")}
          </span>
          <span className="record__who">
            {row.customerName}
            {row.customerPhone ? ` · ${row.customerPhone}` : ""}
            {row.itemsCount ? ` · ${row.approvedCount}/${row.itemsCount}` : ""}
          </span>
        </span>

        <span className="record__tags">{this.renderTags(row, queue)}</span>

        {/* A confirmed appointment has exactly one next step, so it is offered
            as a button rather than left behind a dialog. The row itself still
            opens the car and its history, which is worth a look first. */}
        {queue.key === "acceptedAppointments" && row.bookingId ? (
          <button
            type="button"
            className="record__action"
            title={strings.start_service_hint}
            onClick={(event) => {
              event.stopPropagation();
              this.startService(row);
            }}
          >
            <i className="nc-icon nc-button-play" />
            {strings.start_service}
          </button>
        ) : (
          <i className="nc-icon nc-minimal-right record__go" />
        )}
      </li>
    );
  }

  renderRecords(data) {
    const { focus } = this.state;
    const queue = QUEUES.find((item) => item.key === focus) || QUEUES[0];
    const rows = data[queue.key] || [];

    return (
      <div className="panel" style={toneVars(queue.tone)}>
        <div className="record-panel__head">
          <span className="record-panel__dot" />
          <h5 className="record-panel__title">{queue.label()}</h5>
          <span className="record-panel__hint">{queue.hint()}</span>
          <span className="record-panel__total">{rows.length}</span>
        </div>

        {rows.length === 0 ? (
          <EmptyState
            icon={queue.icon}
            title={strings.queue_empty}
            text={strings.queue_empty_hint}
          />
        ) : (
          <ul className="record-list">
            {rows.map((row) => this.renderRecord(row, queue))}
          </ul>
        )}
      </div>
    );
  }

  renderShortcuts() {
    const { selectedBranch, garageScoped } = this.state;
    // a garage login already carries these in its top nav, so the dashboard
    // only offers them to an admin who drilled into one garage
    if (!selectedBranch || garageScoped) return null;
    const sections = getGarageSections(selectedBranch);
    if (sections.length === 0) return null;

    return (
      <div className="panel">
        <div className="panel__head">
          <h5 className="panel__title">{strings.garage}</h5>
        </div>
        <div className="dashboard-shortcuts">
          {sections.map((section) => (
            <button
              type="button"
              key={section.key}
              className="garage-action"
              onClick={() => this.props.history.push(section.path)}
            >
              <i className={section.icon} />
              {section.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  renderGarageDay(data) {
    return (
      <>
        {this.renderTiles(data.summary, true)}
        {this.renderRecords(data)}
        <div className="panel-grid mt-3">{this.renderShortcuts()}</div>
      </>
    );
  }

  // ─── every garage ──────────────────────────────────────────────────────────

  /** The merchant's own figures, which no single garage can answer. */
  renderFacts(totals) {
    const facts = [
      {
        key: "garages",
        icon: "nc-icon nc-bank",
        value: totals.garages,
        label: strings.garages,
      },
      {
        key: "workers",
        icon: "nc-icon nc-single-02",
        value: totals.workers,
        label: strings.merchant_workers,
      },
      {
        key: "services",
        icon: "nc-icon nc-settings",
        value: totals.services,
        label: strings.services,
      },
      {
        key: "rating",
        icon: "nc-icon nc-favourite-28",
        value:
          totals.ratingAverage === null || totals.ratingAverage === undefined
            ? "—"
            : totals.ratingAverage.toFixed(1),
        label: totals.ratingCount
          ? `${totals.ratingCount} ${strings.ratings_hint}`
          : strings.no_rating,
      },
      {
        key: "overdue",
        icon: "nc-icon nc-time-alarm",
        value: totals.overdue,
        label: strings.overdue_deliveries,
        late: true,
      },
    ];

    return (
      <div className="overview-facts">
        {facts.map((fact) => (
          <span
            key={fact.key}
            className={`overview-fact${
              fact.late && fact.value > 0 ? " is-late" : ""
            }`}
          >
            <i className={fact.icon} />
            <span className="overview-fact__value">{fact.value}</span>
            <span className="overview-fact__label">{fact.label}</span>
          </span>
        ))}
      </div>
    );
  }

  /**
   * One row per garage, the eight queues as eight columns. Busiest first: an
   * admin opens this to find the garage that needs them, not to read an
   * alphabetical list.
   */
  renderGarageTable(garages) {
    const rows = [...garages].sort(
      (a, b) => b.overdue - a.overdue || b.load - a.load
    );

    return (
      <div className="panel">
        <div className="record-panel__head">
          <h5 className="record-panel__title">{strings.garages_by_load}</h5>
          <span className="record-panel__hint">
            {strings.garages_by_load_hint}
          </span>
          <span className="record-panel__total">{garages.length}</span>
        </div>

        {garages.length === 0 ? (
          <EmptyState icon="nc-icon nc-bank" title={strings.no_garages} />
        ) : (
          <div className="overview-table__scroll">
            <table className="overview-table">
              <thead>
                <tr>
                  <th rowSpan={2}>{strings.garage}</th>
                  {COLUMN_GROUPS.map((group) => (
                    <th
                      key={group.key}
                      className="is-group"
                      colSpan={
                        QUEUES.filter((queue) => queue.group === group.key)
                          .length
                      }
                    >
                      {group.label()}
                    </th>
                  ))}
                  <th rowSpan={2}>{strings.overdue}</th>
                  <th rowSpan={2}>{strings.garage_load}</th>
                </tr>
                <tr>
                  {QUEUES.map((queue) => (
                    <th key={queue.key} title={queue.hint()}>
                      {strings[`q_short_${queue.key}`] || queue.label()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((garage) => (
                  <tr
                    key={garage.branchId}
                    title={strings.open_garage}
                    onClick={() => this.selectBranch(garage.branchId)}
                  >
                    <td>
                      <span className="overview-garage__name">
                        {garage.branchName}
                      </span>
                      <span className="overview-garage__meta">
                        {garage.workers} {strings.staff_short} ·{" "}
                        {garage.services} {strings.services}
                        {garage.ratingCount
                          ? ` · ★ ${garage.ratingAverage.toFixed(1)} (${
                              garage.ratingCount
                            })`
                          : ""}
                      </span>
                    </td>
                    {QUEUES.map((queue) => {
                      const count = garage.summary[queue.key] || 0;
                      return (
                        <td key={queue.key}>
                          <span
                            className={`overview-count${
                              count ? "" : " is-zero"
                            }`}
                            style={toneVars(queue.tone)}
                          >
                            {count}
                          </span>
                        </td>
                      );
                    })}
                    <td>
                      {garage.overdue > 0 ? (
                        <span className="overview-late">{garage.overdue}</span>
                      ) : (
                        <span className="overview-count is-zero">0</span>
                      )}
                    </td>
                    <td>
                      <span className="overview-total">{garage.load}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  /**
   * The one list of records on the overview. Everything else is a count an
   * admin can act on later; a car promised back and still standing here is
   * one they have to act on now.
   */
  renderOverdue(rows) {
    const queue = QUEUES.find((item) => item.key === "unpaid");

    return (
      <div className="panel mt-3" style={toneVars("unpaid")}>
        <div className="record-panel__head">
          <span className="record-panel__dot" />
          <h5 className="record-panel__title">{strings.overdue_deliveries}</h5>
          <span className="record-panel__hint">
            {strings.overdue_deliveries_hint}
          </span>
          <span className="record-panel__total">{rows.length}</span>
        </div>

        {rows.length === 0 ? (
          <EmptyState
            icon="nc-icon nc-check-2"
            title={strings.no_overdue}
            text={strings.no_overdue_hint}
          />
        ) : (
          <ul className="record-list">
            {rows.map((row) => (
              <li
                key={row.key}
                className="record"
                role="button"
                tabIndex={0}
                onClick={() => this.openRecord(row)}
                onKeyDown={(event) =>
                  event.key === "Enter" && this.openRecord(row)
                }
              >
                <span className="record__time">
                  {row.deliveryAt
                    ? new Date(row.deliveryAt).toLocaleDateString()
                    : "—"}
                  <small>{row.branchName}</small>
                </span>

                <span className="record__main">
                  <span className="record__plate">
                    {row.plateNumber || "—"}
                  </span>
                  <span className="record__vehicle">
                    {[row.vehicleMake, row.vehicleModel]
                      .filter(Boolean)
                      .join(" ")}
                  </span>
                  <span className="record__who">
                    {row.customerName}
                    {row.customerPhone ? ` · ${row.customerPhone}` : ""}
                  </span>
                </span>

                <span className="record__tags">
                  {this.renderTags(row, queue)}
                </span>
                <i className="nc-icon nc-minimal-right record__go" />
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  renderOverview(data) {
    return (
      <>
        {this.renderFacts(data.totals)}
        {this.renderTiles(data.totals, false)}
        {this.renderGarageTable(data.garages || [])}
        {this.renderOverdue(data.overdueRecords || [])}
      </>
    );
  }

  render() {
    const {
      isLoading,
      failed,
      garageData,
      merchantData,
      merchants,
      branches,
      selectedMerchant,
      selectedBranch,
      garageScoped,
      detailOf,
    } = this.state;

    const showsGarage = Boolean(selectedBranch || garageScoped);
    const data = showsGarage ? garageData : merchantData;

    return (
      <div className="content dashboard-page">
        <PageHeader
          icon="nc-icon nc-chart-bar-32"
          title={strings.dashboard}
          subtitle={
            showsGarage ? strings.dashboard_day_hint : strings.dashboard_admin_hint
          }
        >
          {!garageScoped &&
            !sessionStorage.getItem("merchantId") &&
            merchants.length !== 1 && (
              <div className="page-head__select">
                <SelectRow
                  hidelabel
                  name="selectedMerchant"
                  defaultValue={selectedMerchant}
                  data={merchants}
                  changeInput={(e) => this.selectMerchant(e.value)}
                  placeholder={strings.merchant}
                />
              </div>
            )}
          {!garageScoped && (
            <div className="page-head__select">
              <SelectRow
                hidelabel
                key={selectedMerchant || "default"}
                name="selectedBranch"
                defaultValue={selectedBranch}
                data={[{ value: "", label: strings.all_garages }, ...branches]}
                changeInput={(e) => this.selectBranch(e.value)}
                placeholder={strings.all_garages}
              />
            </div>
          )}
        </PageHeader>

        {isLoading && !data ? (
          this.renderProgress()
        ) : failed ? (
          <EmptyState
            icon="nc-icon nc-alert-circle-i"
            title={strings.dashboard_unavailable}
            text={strings.dashboard_unavailable_hint}
          >
            <button type="button" className="garage-action" onClick={this.load}>
              <i className="nc-icon nc-refresh-69" />
              {strings.retry}
            </button>
          </EmptyState>
        ) : !data ? (
          <EmptyState
            icon="nc-icon nc-bank"
            title={strings.select_merchant_first}
          />
        ) : showsGarage ? (
          this.renderGarageDay(data)
        ) : (
          this.renderOverview(data)
        )}

        <FormModal
          isOpen={Boolean(detailOf)}
          onClose={() => this.setState({ detailOf: null })}
          icon="nc-icon nc-car"
          title={strings.appointment_detail}
          subtitle={detailOf ? `${detailOf.date} · ${detailOf.time}` : ""}
        >
          {detailOf && (
            <AppointmentDetail
              appointmentId={detailOf.appointmentId}
              branchId={detailOf.branchId}
              onChanged={this.load}
              onStartService={(booking) =>
                this.startService({
                  bookingId: booking.id,
                  branchId: detailOf.branchId,
                })
              }
              onOpenWorkOrder={(row) =>
                this.props.history.push({
                  pathname: `/admin/work-orders/${row.id}/manage`,
                  state: {
                    workOrder: { id: row.id },
                    booking: row.bookingId ? { id: row.bookingId } : null,
                  },
                })
              }
            />
          )}
        </FormModal>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  loadingAction: (payload) => dispatch(loadingAction(payload)),
});

export default connect(null, mapDispatchToProps)(GarageDashboard);
